import React, { useMemo } from 'react';
import { useForm } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
    Combobox,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
    ComboboxValue,
    useComboboxAnchor,
} from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import ValidationError from '../../components/ValidationError';
import { useTrans } from '../../hooks/useTrans';

function FormRow({ label, required = false, children }) {
    return (
        <div className="space-y-1">
            <label className="text-sm font-bold text-[#003545]">
                {label}{required && ' *'}
            </label>
            <div>
                {children}
            </div>
        </div>
    );
}

function CheckboxRow({ children }) {
    return (
        <div>
            {children}
        </div>
    );
}

export function UserForm({ user, roles, debtors, languages, emailDomain, canBeManaged = true, isFederated = false }) {
    const translate = useTrans();
    const rolesAnchorRef = useComboboxAnchor();
    const debtorsAnchorRef = useComboboxAnchor();
    const isEditing = !!user;
    const isReadonly = !canBeManaged;

    const disabledStyle = 'bg-[#cecece]! dark:bg-[#cecece]! cursor-not-allowed';
    const chipsClassName = `min-h-[40px] w-full rounded-sm border-[#bbb] bg-white dark:bg-white shadow-none ${isReadonly ? disabledStyle : ''}`;
    const debtorChipsClassName = `min-h-[40px] w-full rounded-sm border-[#bbb] bg-white dark:bg-white shadow-none ${isReadonly || isFederated ? disabledStyle : ''}`;
    const triggerDisabledClassName = isReadonly ? `${disabledStyle} opacity-100!` : '';

    const roleItems = useMemo(() => roles.map((role) => ({
        value: String(role.id),
        label: role.name,
    })), [roles]);

    const debtorItems = useMemo(() => debtors.map((debtor) => ({
        value: String(debtor.id),
        label: debtor.name,
    })), [debtors]);

    const languageOptions = useMemo(() => languages.map((l) => ({
        value: String(l.id),
        label: translate(l.name),
    })), [languages, translate]);

    const { data, setData, post, put, processing, errors } = useForm({
        first_name: user?.first_name ?? '',
        last_name: user?.last_name ?? '',
        roles: user?.roles?.map(String) ?? [],
        debtors: user?.debtors?.map(String) ?? (debtors.length === 1 ? [String(debtors[0].id)] : []),
        phone_number: user?.phone_number ?? '',
        mobile_number: user?.mobile_number ?? '',
        email: user?.email ?? '',
        language_id: user?.language_id ? String(user.language_id) : (languages.length > 0 ? String(languages[0].id) : ''),
        is_active: user?.is_active ?? false,
        ...(user?.username ? { username: user.username } : {}),
    });

    const selectedRoleItems = roleItems.filter((item) => data.roles.includes(item.value));
    const selectedDebtorItems = debtorItems.filter((item) => data.debtors.includes(item.value));
    const selectedLanguage = languageOptions.find((l) => l.value === String(data.language_id)) ?? null;

    function handleSubmit(e) {
        e.preventDefault();
        if (isEditing) {
            put(`/gebruikers/${user.id}`);
        } else {
            post('/gebruikers');
        }
    }

    const title = isEditing
        ? translate('Bewerk :name', { name: user.name })
        : translate('Gebruiker uitnodigen');

    return (
        <div className="bg-white rounded-[3px] border border-[#bbb]">
            <div className="p-[30px] pb-0">
                <h3 className="text-2xl font-bold text-[#003545] mb-6">
                    {title}
                </h3>
            </div>

            <div className="px-[30px] pb-[30px]">
                <form onSubmit={handleSubmit} className="space-y-4">
                    {errors.api && (
                        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-800">
                            {errors.api}
                        </div>
                    )}

                    <FormRow label={translate('Voornaam')} required>
                        <Input
                            value={data.first_name}
                            onChange={(e) => setData('first_name', e.target.value)}
                            readOnly={isReadonly}
                            className="font-normal"
                        />
                        <ValidationError message={errors.first_name} />
                    </FormRow>

                    <FormRow label={translate('Achternaam')} required>
                        <Input
                            value={data.last_name}
                            onChange={(e) => setData('last_name', e.target.value)}
                            required
                            readOnly={isReadonly}
                            className="font-normal"
                        />
                        <ValidationError message={errors.last_name} />
                    </FormRow>

                    <FormRow label={translate('Rol(len)')} required>
                        <Combobox
                            items={roleItems}
                            multiple
                            value={selectedRoleItems}
                            onValueChange={(items) => setData('roles', items.map((i) => i.value))}
                            itemToStringValue={(item) => item.label}
                            disabled={isReadonly}
                        >
                            <ComboboxChips ref={rolesAnchorRef} className={chipsClassName}>
                                <ComboboxValue>
                                    {(values) => (
                                        <React.Fragment>
                                            {values.map((item) => (
                                                <ComboboxChip key={item.value}>{item.label}</ComboboxChip>
                                            ))}
                                            <ComboboxChipsInput />
                                        </React.Fragment>
                                    )}
                                </ComboboxValue>
                            </ComboboxChips>
                            <ComboboxContent anchor={rolesAnchorRef} >
                                <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                <ComboboxList>
                                    {(item) => (
                                        <ComboboxItem key={item.value} value={item}>
                                            {item.label}
                                        </ComboboxItem>
                                    )}
                                </ComboboxList>
                            </ComboboxContent>
                        </Combobox>
                        <ValidationError message={errors.roles} />
                    </FormRow>

                    <FormRow label={translate('Debiteuren')} required={!isFederated}>
                        <Combobox
                            items={debtorItems}
                            multiple
                            value={selectedDebtorItems}
                            onValueChange={(items) => setData('debtors', items.map((i) => i.value))}
                            itemToStringValue={(item) => item.label}
                            disabled={isReadonly || isFederated}
                        >
                            <ComboboxChips ref={debtorsAnchorRef} className={debtorChipsClassName}>
                                <ComboboxValue>
                                    {(values) => (
                                        <React.Fragment>
                                            {values.map((item) => (
                                                <ComboboxChip key={item.value}>{item.label}</ComboboxChip>
                                            ))}
                                            <ComboboxChipsInput />
                                        </React.Fragment>
                                    )}
                                </ComboboxValue>
                            </ComboboxChips>
                            <ComboboxContent anchor={debtorsAnchorRef} >
                                <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                <ComboboxList>
                                    {(item) => (
                                        <ComboboxItem key={item.value} value={item}>
                                            {item.label}
                                        </ComboboxItem>
                                    )}
                                </ComboboxList>
                            </ComboboxContent>
                        </Combobox>
                        <ValidationError message={errors.debtors} />
                    </FormRow>

                    <FormRow label={translate('Telefoon')}>
                        <Input
                            value={data.phone_number}
                            onChange={(e) => setData('phone_number', e.target.value)}
                            readOnly={isReadonly}
                            className="font-normal"
                        />
                        <ValidationError message={errors.phone_number} />
                    </FormRow>

                    <FormRow label={translate('Mobiel nummer')}>
                        <Input
                            value={data.mobile_number}
                            onChange={(e) => setData('mobile_number', e.target.value)}
                            readOnly={isReadonly}
                            className="font-normal"
                        />
                        <ValidationError message={errors.mobile_number} />
                    </FormRow>

                    <FormRow label={translate('E-mailadres')} required>
                        <Input
                            type="email"
                            value={data.email}
                            onChange={(e) => setData('email', e.target.value)}
                            required
                            readOnly={isReadonly || isFederated}
                            className="font-normal"
                        />
                        <ValidationError message={errors.email} />
                        {!errors.email && emailDomain && !isFederated && (
                            <p className="mt-1 text-sm text-[#003545]/60">
                                {translate('Het e-mailadres moet eindigen op het domein: @:values.', { values: emailDomain })}
                            </p>
                        )}
                    </FormRow>

                    <hr className="border-[#bbb] my-7" />
                    <h5 className="text-lg font-bold text-[#003545]">{translate('Gebruikersinstellingen')}</h5>

                    {isEditing && data.username !== undefined && (
                        <FormRow label={translate('Gebruikersnaam')} required>
                            <Input
                                value={data.username}
                                onChange={(e) => setData('username', e.target.value)}
                                readOnly={isReadonly}
                                className="font-normal"
                            />
                            <ValidationError message={errors.username} />
                        </FormRow>
                    )}

                    <FormRow label={translate('Taal')}>
                        <Combobox
                            items={languageOptions}
                            value={selectedLanguage}
                            onValueChange={(item) => setData('language_id', item?.value ?? '')}
                            itemToStringValue={(item) => item.label}
                            disabled={isReadonly}
                        >
                            <ComboboxTrigger render={
                                <Button variant="outline" className={`w-full justify-between font-normal rounded-sm border-[#bbb] shadow-none ${triggerDisabledClassName}`}>
                                    <ComboboxValue placeholder={translate('Selecteer taal...')} />
                                </Button>
                            } />
                            <ComboboxContent>
                                <ComboboxInput showTrigger={false} placeholder={translate('Zoeken...')} />
                                <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                <ComboboxList>
                                    {(item) => (
                                        <ComboboxItem key={item.value} value={item}>
                                            {item.label}
                                        </ComboboxItem>
                                    )}
                                </ComboboxList>
                            </ComboboxContent>
                        </Combobox>
                        <ValidationError message={errors.language_id} />
                    </FormRow>

                    <CheckboxRow>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="is_active"
                                checked={data.is_active}
                                onCheckedChange={(checked) => setData('is_active', checked)}
                                disabled={isReadonly || isFederated}
                            />
                            <label htmlFor="is_active" className="text-sm text-[#003545] cursor-pointer">
                                {translate('Is een actieve gebruiker')}
                            </label>
                        </div>
                    </CheckboxRow>

                    <hr className="border-[#bbb] my-7" />

                    <div>
                        <Button variant="primary" type="submit" disabled={processing || isReadonly}>
                            {translate('Opslaan')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
